import { useEffect, useState } from "react";
import { Box, Grid } from "@mui/system";
import {
  Button,
  Dialog,
  DialogContent,
  IconButton,
  TablePagination,
  TextField,
  Tooltip,
} from "@mui/material";
import FirstPageRoundedIcon from "@mui/icons-material/FirstPageRounded";
import LastPageRoundedIcon from "@mui/icons-material/LastPageRounded";
import ChevronLeftRoundedIcon from "@mui/icons-material/ChevronLeftRounded";
import ChevronRightRoundedIcon from "@mui/icons-material/ChevronRightRounded";
import CloudDoneIcon from "@mui/icons-material/CloudDone";
import ModeEditOutlineIcon from "@mui/icons-material/ModeEditOutline";
import HourglassBottomIcon from "@mui/icons-material/HourglassBottom";
import VisibilityIcon from "@mui/icons-material/Visibility";
import styles from "./Settlement.module.css";
import {
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Typography,
} from "@mui/material";
import { DesktopDatePicker, LocalizationProvider } from "@mui/x-date-pickers";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import dayjs from "dayjs";
import {
  useLoaderContext,
  useToastContext,
} from "../../../../Context/ToastContext";
import { useUserContext } from "../../../../Context/UserContext";
import { Helmet } from "react-helmet";
import Dropdown1 from "../../Dropdown/Dropdown1";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import ChevronLeftIcon from "@mui/icons-material/ChevronLeft";
import ChevronRightIcon from "@mui/icons-material/ChevronRight";
import { apiGet, apiPost, apiPut } from "../../../../utils/ApiManager";

const viewBookings = process.env.REACT_APP_FETCH_RESTAURANTS_VIEW_BOOKING;
const fetchSettlements = process.env.REACT_APP_FETCH_SETTLEMENTS;
const createSettlements = process.env.REACT_APP_CREATE_SETTLEMENT;
const updateSettlements = process.env.REACT_APP_UPDATE_SETTLEMENT;

const Settlement = () => {
  const [page, setPage] = useState(1);
  const { setLoading } = useLoaderContext();
  const [rowsPerPage, setRowsPerPage] = useState(5);
  const [totalCount, setTotalCount] = useState(100);
  const [addingSettlement, setAddingSettlement] = useState(false);
  const [updatingSettlement, setUpdatingSettlement] = useState(false);
  const [currentSettlementData, setCurrentSettlementData] = useState({});
  const { showToast } = useToastContext();
  const { requestConfirmation } = useUserContext();
  const [restaurantDropdown, setRestaurantDropdown] = useState([]);
  const [settlementTypeDropdown, setSettlementTypeDropdown] = useState([]);
  const [createDisabled, setCreateDisabled] = useState(false);
  const [errors, setErrors] = useState({
    settlementType: "",
    startDate: "",
    endDate: "",
    restaurantId: "",
  });
  const [filter, setFilter] = useState({
    settlementType: "",
    restaurantId: "",
  });

  const [newSettlement, setNewSettlement] = useState({
    settlementType: "weekly",
    restaurantId: "",
    startDate: null,
    endDate: null,
  });

  const [startDate, setStartDate] = useState("");
  const [endDate, setEndDate] = useState("");

  const [settlements, setSettlements] = useState([]);

  const handleChange = (e) => {
    if (["paymentMode", "transactionId"].includes(e.target.name)) {
      setCurrentSettlementData({
        ...currentSettlementData,
        [e.target.name]: e.target.value,
      });
      return;
    }
    setNewSettlement({ ...newSettlement, [e.target.name]: e.target.value });
  };
  const handleChangePage = (event, newPage) => {
    setPage(newPage + 1);
  };

  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value));
    setPage(1);
  };

  const fetchDropdown = async () => {
    try {
      const response = await apiGet(`${viewBookings}?settlementTypes=${true}`);
      if (response?.success) {
        setRestaurantDropdown(response?.data?.restaurantList || []);
        setSettlementTypeDropdown(response?.data?.settlementTypes || []);
      } else {
        showToast(response?.message || "Error fetching dropdown.", "error");
      }
    } catch (error) {
      showToast(
        error?.response?.message ||
          error?.message ||
          "Error fetching dropdown.",
        "error"
      );
    }
  };

  const validateFields = async (data = newSettlement) => {
    try {
      let newErrors = {};
      let valid = true;

      Object.keys(data).forEach((key) => {
        if (!data[key]) {
          newErrors[key] = "This field is required!";
          valid = false;
        }
      });

      const today = new Date();
      today.setHours(0, 0, 0, 0);

      const startDate = new Date(data?.startDate);
      const endDate = new Date(data?.endDate);

      if (startDate.setHours(0, 0, 0, 0) > today) {
        newErrors.startDate = "Start date should be in the past!";
        valid = false;
      }

      if (endDate.setHours(23, 59, 59, 999) > today) {
        newErrors.endDate = "End date should be in the past!";
        valid = false;
      }

      // Ensure endDate is after startDate
      if (startDate && endDate && endDate < startDate) {
        newErrors.endDate = "End date must be after start date!";
        valid = false;
      }

      setErrors(newErrors);
      setCreateDisabled(!valid);
      return valid;
    } catch (error) {
      return false;
    }
  };

  const createSettlement = async () => {
    try {
      if (!newSettlement?.restaurantId) {
        showToast("Please select restaurant to create Settlement", "error");
        return;
      }
      const check = await requestConfirmation(`Want to create settlement for 
        <br/><b>${
          restaurantDropdown?.find(
            (restaurant) => restaurant.value === newSettlement?.restaurantId
          )?.label
        }</b>
        <br/><b>${new Date(
          newSettlement?.startDate
        ).toDateString()}</b> to <b>${new Date(
        newSettlement?.endDate
      ).toDateString()}</b> ?`);
      if (!check) return;
      const data = {
        startDate: new Date(newSettlement?.startDate).setHours(0, 0, 0, 0),
        endDate: new Date(newSettlement?.endDate).setHours(23, 59, 59, 999),
        restaurantId: newSettlement?.restaurantId,
      };

      setLoading(true);
      const response = await apiPost(`${createSettlements}`, data);

      if (response?.success) {
        showToast(
          response?.message || "Settlement created successfully.",
          "success"
        );
        getSettlements();
        setAddingSettlement(false);
      } else {
        showToast(response?.message || "Error creating settlement.", "error");
      }
    } catch (error) {
      showToast(
        error?.response?.message ||
          error?.message ||
          "Error creating settlement.",
        "error"
      );
    } finally {
      setLoading(false);
    }
  };

  const getWeekRangeTitle = (startDateStr, endDateStr) => {
    if (!startDateStr || !endDateStr) return "";

    const startDate = new Date(startDateStr);
    const endDate = new Date(endDateStr);

    const options = { day: "numeric", month: "short" };

    const startFormatted = startDate
      .toLocaleDateString("en-US", options)
      .toUpperCase();
    const endFormatted = endDate
      .toLocaleDateString("en-US", options)
      .toUpperCase();

    return `REPORT OF ${startFormatted} TO ${endFormatted}`;
  };

  const getSettlements = async (newSearch) => {
    try {
      let pageNo = page;
      if (newSearch) {
        pageNo = 1;
        setPage(1);
      }
      const fullParams = {
        ...filter,
        pageNo: pageNo,
        pageSize: rowsPerPage,
      };

      // Convert to query string
      const queryString = new URLSearchParams(fullParams).toString();
      const response = await apiGet(`${fetchSettlements}?${queryString}`);
      if (response?.success) {
        setSettlements(response?.data || []);
        setTotalCount(response?.totalCount || 0);
      } else {
        showToast(response?.message || "Error fetching settlements.", "error");
      }
    } catch (error) {
      showToast(
        error?.response?.message ||
          error?.message ||
          "Error fetching settlements.",
        "error"
      );
    }
  };

  const handleDateChange = (value, name) => {
    const date = dayjs(value);

    if (name === "paidOn") {
      setCurrentSettlementData((prev) => ({ ...prev, paidOn: date }));
      return;
    }

    setNewSettlement((prev) => {
      const updated = { ...prev, [name]: date };
      // validate after setting state
      setTimeout(() => validateFields(updated), 0);
      return updated;
    });
  };

  const updateSettlement = async () => {
    const result = await requestConfirmation(
      "Want to mark this settlement as settled?"
    );
    if (!result) return;
    try {
      const response = await apiPut(
        `${updateSettlements}`,
        currentSettlementData
      );
      if (response?.success) {
        showToast(
          response?.message || "Settlement updated successfully.",
          "success"
        );
        getSettlements();
      } else {
        showToast(response?.message || "Error updating settlement.", "error");
      }
    } catch (error) {
      showToast(
        error?.response?.message ||
          error?.message ||
          "Error updating settlement.",
        "error"
      );
    }
  };
  // const handleNextRange = () => {
  //   if (newSettlement.startDate && newSettlement.endDate) {
  //     const nextStart = new Date(newSettlement.startDate);
  //     const nextEnd = new Date(newSettlement.endDate);

  //     nextStart.setDate(nextStart.getDate() + 7);
  //     nextEnd.setDate(nextEnd.getDate() + 7);
  //     setNewSettlement({
  //       ...newSettlement,
  //       startDate: nextStart,
  //       endDate: nextEnd,
  //     });
  //   }
  // };

  const handleNextRange = () => {
    if (newSettlement.startDate && newSettlement.endDate) {
      if (newSettlement.settlementType === "monthly") {
        const current = new Date(newSettlement.startDate);

        // Move to next month
        const nextMonth = current.getMonth() + 1;
        const year = current.getFullYear() + Math.floor(nextMonth / 12);
        const month = nextMonth % 12;

        const startDate = new Date(year, month, 1); // 1st day of next month
        const endDate = new Date(year, month + 1, 0); // last day of next month

        setNewSettlement({
          ...newSettlement,
          startDate,
          endDate,
        });
      } else {
        // Weekly behavior
        const nextStart = new Date(newSettlement.startDate);
        const nextEnd = new Date(newSettlement.endDate);
        nextStart.setDate(nextStart.getDate() + 7);
        nextEnd.setDate(nextEnd.getDate() + 7);

        setNewSettlement({
          ...newSettlement,
          startDate: nextStart,
          endDate: nextEnd,
        });
      }
    }
  };

  // const handlePrevRange = () => {
  //   if (newSettlement.startDate && newSettlement.endDate) {
  //     const prevStart = new Date(newSettlement.startDate);
  //     const prevEnd = new Date(newSettlement.endDate);
  //     if (newSettlement.settlementType === "monthly") {
  //       prevStart.setMonth(prevStart.getMonth() - 1);
  //       prevEnd.setMonth(prevEnd.getMonth() - 1);
  //     } else {
  //       prevStart.setDate(prevStart.getDate() - 7);
  //       prevEnd.setDate(prevEnd.getDate() - 7);
  //     }
  //     setNewSettlement({
  //       ...newSettlement,
  //       startDate: prevStart,
  //       endDate: prevEnd,
  //     });
  //   }
  // };

  const handlePrevRange = () => {
    if (newSettlement.startDate && newSettlement.endDate) {
      if (newSettlement.settlementType === "monthly") {
        const current = new Date(newSettlement.startDate);

        // Move to previous month
        const prevMonth = current.getMonth() - 1;
        const year = current.getFullYear() + Math.floor(prevMonth / 12);
        const month = (prevMonth + 12) % 12; // Ensure valid month (0–11)

        const startDate = new Date(year, month, 1); // 1st day of previous month
        const endDate = new Date(year, month + 1, 0); // last day of previous month

        setNewSettlement({
          ...newSettlement,
          startDate,
          endDate,
        });
      } else {
        // Weekly behavior
        const prevStart = new Date(newSettlement.startDate);
        const prevEnd = new Date(newSettlement.endDate);
        prevStart.setDate(prevStart.getDate() - 7);
        prevEnd.setDate(prevEnd.getDate() - 7);

        setNewSettlement({
          ...newSettlement,
          startDate: prevStart,
          endDate: prevEnd,
        });
      }
    }
  };

  const getRecentRange = () => {
    const today = new Date();

    if (newSettlement?.settlementType === "monthly") {
      // Calculate previous month
      const prevMonth = today.getMonth() - 1;
      const year = today.getFullYear() + Math.floor(prevMonth / 12);
      const month = (prevMonth + 12) % 12;

      const startDate = new Date(year, month, 1);
      const endDate = new Date(year, month + 1, 0); // Last day of the previous month

      return { startDate, endDate };
    } else {
      // Calculate previous full week (Sunday–Saturday)
      const dayOfWeek = today.getDay();
      const prevSunday = new Date(today);
      prevSunday.setDate(today.getDate() - dayOfWeek - 7);

      const prevSaturday = new Date(prevSunday);
      prevSaturday.setDate(prevSunday.getDate() + 6);

      return {
        startDate: prevSunday,
        endDate: prevSaturday,
      };
    }
  };

  const handleEditClick = (settlementData) => {
    setCurrentSettlementData({ ...settlementData });
    setUpdatingSettlement(true);
  };

  // const getRecentRange = () => {
  //   const today = new Date();

  //   // Get current day of week (0 = Sunday, 6 = Saturday)
  //   const dayOfWeek = today.getDay();

  //   // Calculate last Sunday's date (start of previous week)
  //   // Go back dayOfWeek + 7 days to get previous week's Sunday
  //   const prevSunday = new Date(today);
  //   prevSunday.setDate(today.getDate() - dayOfWeek - 7);

  //   // Previous week's Saturday = prevSunday + 6 days
  //   const prevSaturday = new Date(prevSunday);
  //   prevSaturday.setDate(prevSunday.getDate() + 6);

  //   // Format to yyyy-mm-dd for input[type=date]
  //   const formatDate = (date) => date.toISOString().split("T")[0];

  //   return {
  //     startDate: prevSunday,
  //     endDate: prevSaturday,
  //   };
  // };

  useEffect(() => {
    fetchDropdown();
  }, []);

  useEffect(() => {
    const { startDate, endDate } = getRecentRange();
    setNewSettlement({ ...newSettlement, startDate, endDate });
  }, [newSettlement.settlementType]);

  useEffect(() => {
    getSettlements();
  }, [page, rowsPerPage]);

  useEffect(() => {
    getSettlements(true);
  }, [filter.restaurantId]);

  useEffect(() => {
    validateFields();
  }, [newSettlement]);

  return (
    <Box className={styles.mainContainer}>
      <Helmet>
        <title>Settlement Records | DiNEST</title>
        <meta name="description" content="View Payments on DiNEST" />
      </Helmet>
      <Box className={styles.restaurantListContainer}>
        <Typography variant="h4" className={styles.containerTitle}>
          Settlement Records
        </Typography>

        <Typography
          variant="h5"
          textAlign="center"
          color="#207d46"
          fontWeight="bold"
        >
          {getWeekRangeTitle(startDate, endDate)}
        </Typography>

        <div className={styles.excelBtnContainer}>
          <Grid className={styles.inputGroup}>
            <Dropdown1
              options={restaurantDropdown}
              name="restaurantId"
              value={filter.restaurantId}
              onChange={(e) =>
                setFilter({ ...filter, restaurantId: e.target.value })
              }
              label={"Restaurant"}
            />
          </Grid>
        </div>
        {/* Search Bar */}
        <Button
          onClick={() => setAddingSettlement(true)}
          variant="contained"
          className={styles.settlementButton}
        >
          Create Settlement
        </Button>
        {settlements?.length > 0 ? (
          <>
            {/* <div className={styles.searchBarContainer}>
              <TextField
                label="Search Restaurant by name"
                variant="outlined"
                fullWidth
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className={styles.searchBar}
              />
            </div> */}

            <TableContainer className={styles.tableContainer} component={Paper}>
              <Table aria-label="restaurant table">
                <TableHead>
                  <TableRow>
                    <TableCell className={styles.tableHead}>Sr. No</TableCell>
                    <TableCell className={styles.tableHead}>
                      Settlement ID
                    </TableCell>
                    <TableCell className={styles.tableHead}>
                      Restaurant Name
                    </TableCell>
                    <TableCell className={styles.tableHead}>
                      Total Bookings
                    </TableCell>
                    <TableCell className={styles.tableHead}>
                      Sub Total Amount ( र )
                    </TableCell>
                    <TableCell className={styles.tableHead}>
                      Sub Total Amount (includes Gst)
                    </TableCell>
                    <TableCell className={styles.tableHead}>
                      Dinest Commition (with GSt)
                    </TableCell>
                    <TableCell className={styles.tableHead}>
                      Restaurant Payout
                    </TableCell>
                    <TableCell className={styles.tableHead}>
                      Settlement From
                    </TableCell>
                    <TableCell className={styles.tableHead}>
                      Settlement Till
                    </TableCell>
                    <TableCell className={styles.tableHead}>
                      Settlement Status{" "}
                    </TableCell>
                    <TableCell className={styles.tableHead}>Actions </TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {settlements &&
                    settlements?.map((data, index) => (
                      <TableRow key={data.restaurantId}>
                        <TableCell className={styles.tableData}>
                          {rowsPerPage * (page - 1) + index + 1}
                        </TableCell>
                        <TableCell className={styles.tableData}>
                          {data?.settlementId || "N/A"}
                        </TableCell>
                        <TableCell
                          className={styles.tableData}
                          sx={{ maxWidth: 150 }}
                        >
                          <Tooltip
                            title={data?.restaurant?.restaurantName || "N/A"}
                            arrow
                          >
                            <Typography
                              style={{
                                display: "inline-block",
                                whiteSpace: "nowrap",
                                overflow: "hidden",
                                textOverflow: "ellipsis",
                                maxWidth: 100,
                              }}
                            >
                              {data?.restaurant?.restaurantName || "N/A"}
                            </Typography>
                          </Tooltip>
                        </TableCell>
                        <TableCell className={styles.tableData}>
                          {data?.totalBookings || "N/A"}
                        </TableCell>
                        <TableCell className={styles.tableData}>
                          {data?.totalAmountWithoutGst || "N/A"}
                        </TableCell>
                        <TableCell className={styles.tableData}>
                          {data?.totalAmountReceived || "N/A"}
                        </TableCell>
                        <TableCell className={styles.tableData}>
                          {data?.dinestCommitionWithGst || "N/A"}
                        </TableCell>
                        <TableCell className={styles.tableData}>
                          {data?.restaurantPayout
                            ? data?.restaurantPayout
                            : "N/A"}
                        </TableCell>
                        <TableCell className={styles.tableData}>
                          {data?.settlementFrom
                            ? new Date(data?.settlementFrom).toLocaleDateString(
                                "en-GB"
                              )
                            : "N/A"}
                        </TableCell>
                        <TableCell className={styles.tableData}>
                          {data?.settlementTill
                            ? new Date(data?.settlementTill).toLocaleDateString(
                                "en-GB"
                              )
                            : "N/A"}
                        </TableCell>

                        <TableCell className={styles.tableData}>
                          {" "}
                          {data?.isSettled ? (
                            <CloudDoneIcon sx={{ color: "#207d46" }} />
                          ) : (
                            <HourglassBottomIcon sx={{ color: "#FFB700" }} />
                          )}
                        </TableCell>
                        <TableCell className={styles.tableData}>
                          {" "}
                          <IconButton onClick={() => handleEditClick(data)}>
                            {" "}
                            {data?.isSettled ? (
                              <VisibilityIcon sx={{ color: "#207d46" }} />
                            ) : (
                              <ModeEditOutlineIcon sx={{ color: "#207d46" }} />
                            )}
                          </IconButton>
                        </TableCell>
                      </TableRow>
                    ))}
                </TableBody>
              </Table>
            </TableContainer>

            <div className={styles.paginationContainer}>
              <TablePagination
                className={`${styles.pagination}`}
                rowsPerPageOptions={[2, 5, 10, 15]}
                count={totalCount}
                rowsPerPage={rowsPerPage}
                page={page - 1}
                onPageChange={handleChangePage}
                onRowsPerPageChange={handleChangeRowsPerPage}
                sx={{
                  "& .MuiTablePagination-actions button": {
                    backgroundColor: "transparent",
                    color: "white !important",
                    "&:hover": {
                      backgroundColor: "rgba(255, 255, 255, 0.1)",
                    },
                    "&:focus": {
                      outline: "3px solid #2196F3 !important",
                    },
                  },
                  "& .MuiTablePagination-actions button svg": {
                    fill: "white !important",
                  },
                  "& .MuiSelect-root": {
                    color: "white !important",
                  },
                  "& .MuiSelect-icon": {
                    fill: "white !important",
                  },
                }}
                slotProps={{
                  select: {
                    "aria-label": "Rows per page",
                  },
                  actions: {
                    showFirstButton: true,
                    showLastButton: true,
                    slots: {
                      firstPageIcon: FirstPageRoundedIcon,
                      lastPageIcon: LastPageRoundedIcon,
                      nextPageIcon: ChevronRightRoundedIcon,
                      backPageIcon: ChevronLeftRoundedIcon,
                    },
                  },
                }}
              />
            </div>
          </>
        ) : (
          <h2 style={{ color: "red", textAlign: "center", marginTop: "2rem" }}>
            No payment records found !
          </h2>
        )}
      </Box>

      <Dialog
        open={addingSettlement}
        onClose={() => setAddingSettlement(false)}
        fullWidth
        maxWidth="sm"
        className={styles.editContainer}
      >
        {/* Back Button */}
        <div
          onClick={() => setAddingSettlement(false)}
          className={styles.backIconContainer}
        >
          <ArrowBackIcon className={styles.backIcon} />
        </div>

        <Typography variant="h5" className={styles.title}>
          Create Settlement
        </Typography>

        <Box container spacing={2} className={styles.settlementGrid}>
          {/* Date Range Navigation + Date Pickers */}
          <Grid
            item
            xs={12}
            sx={{
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
              gap: 1,
            }}
          >
            {/* Prev Icon Button */}
            <Tooltip
              arrow
              title={`Prev ${
                newSettlement?.settlementType === "monthly" ? "Month" : "Week"
              }`}
            >
              <IconButton
                onClick={handlePrevRange}
                size="large"
                aria-label="previous range"
              >
                <ChevronLeftIcon />
              </IconButton>
            </Tooltip>

            {/* Start Date Picker */}
            <LocalizationProvider dateAdapter={AdapterDayjs}>
              <DesktopDatePicker
                label="Start Date"
                name="startDate"
                inputFormat="DD/MM/YYYY"
                format="DD/MM/YYYY"
                value={
                  newSettlement?.startDate
                    ? dayjs(newSettlement?.startDate)
                    : null
                }
                onChange={(e) => handleDateChange(e, "startDate")}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    fullWidth
                    error={Boolean(errors?.startDate)}
                    helperText={errors?.startDate}
                    size="small"
                  />
                )}
              />

              {/* End Date Picker */}
              <DesktopDatePicker
                label="End Date"
                name="endDate"
                inputFormat="DD/MM/YYYY"
                format="DD/MM/YYYY"
                value={
                  newSettlement?.endDate ? dayjs(newSettlement?.endDate) : null
                }
                onChange={(e) => handleDateChange(e, "endDate")}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    fullWidth
                    error={Boolean(errors?.endDate)}
                    helperText={errors?.endDate}
                    size="small"
                    sx={{ mt: 2 }}
                  />
                )}
              />
            </LocalizationProvider>

            {/* Next Icon Button */}
            <Tooltip
              arrow
              title={`Next ${
                newSettlement?.settlementType === "monthly" ? "Month" : "Week"
              }`}
            >
              <IconButton
                onClick={handleNextRange}
                size="large"
                aria-label="next range"
              >
                <ChevronRightIcon />
              </IconButton>
            </Tooltip>
          </Grid>

          <Grid className={styles.inputGroupGrid}>
            <Dropdown1
              options={settlementTypeDropdown}
              fullWidth
              name="settlementType"
              value={newSettlement.settlementType}
              onChange={handleChange}
              label="Settlement Type"
            />
            {errors?.settlementType && (
              <Typography color="error" variant="caption" mt={0.5}>
                {errors?.settlementType}
              </Typography>
            )}
          </Grid>

          {/* Restaurant Dropdown */}
          <Grid className={styles.inputGroupGrid}>
            <Dropdown1
              options={restaurantDropdown}
              name="restaurantId"
              value={newSettlement.restaurantId}
              onChange={handleChange}
              label={"Restaurant"}
            />
            {errors?.restaurantId && (
              <Typography color="error" variant="caption" mt={0.5}>
                {errors?.restaurantId}
              </Typography>
            )}
          </Grid>

          <Typography
            variant="body1"
            className={styles.settlementContainerTitle}
          >
            Creating Settlement for{" "}
            {new Date(newSettlement?.startDate).toLocaleDateString("en-GB")} -{" "}
            {new Date(newSettlement?.endDate).toLocaleDateString("en-GB")}
          </Typography>

          {/* Create Settlement Button */}
          <Button
            onClick={createSettlement}
            // disabled={createDisabled}
            variant="contained"
            className={styles.saveBtn}
            sx={{ backgroundColor: "#207d46" }}
          >
            Save
          </Button>
        </Box>
      </Dialog>

      <Dialog fullWidth maxWidth="xs" open={updatingSettlement}>
        <div
          onClick={() => setUpdatingSettlement(false)}
          className={styles.backIconContainer}
        >
          <ArrowBackIcon className={styles.backIcon} />
        </div>

        <Typography variant="h6" className={styles.titleSettlementModal}>
          {currentSettlementData?.isSettled ? "View" : "Update"} Settlement
        </Typography>

        <DialogContent className={styles.formScrollable}>
          <Box
            sx={{
              textAlign: "center",
              display: "flex",
              flexDirection: "column",
              gap: 2,
            }}
          >
            <Typography>
              <strong>Settlement Id:</strong>{" "}
              {currentSettlementData?.settlementId ?? "N/A"}
            </Typography>

            <Typography>
              <strong>Restaurant Name:</strong>{" "}
              {currentSettlementData?.restaurant?.restaurantName ?? "N/A"}
            </Typography>

            <Typography>
              <strong>Total Bookings:</strong>{" "}
              {currentSettlementData?.totalBookings ?? "N/A"}
            </Typography>

            <Typography>
              <strong>Total Amount (without GST):</strong>{" "}
              {currentSettlementData?.totalAmountWithoutGst
                ? `₹${currentSettlementData.totalAmountWithoutGst} /-`
                : "N/A"}
            </Typography>

            <Typography>
              <strong>Total Amount Received (with GST):</strong>{" "}
              {currentSettlementData?.totalAmountReceived
                ? `₹${currentSettlementData.totalAmountReceived} /-`
                : "N/A"}
            </Typography>

            <Typography>
              <strong>Dinest Commission Amount:</strong>{" "}
              {currentSettlementData?.dinestCommission
                ? `₹${currentSettlementData.dinestCommission} /-`
                : "N/A"}
            </Typography>

            <Typography>
              <strong>GST on Commission Amount:</strong>{" "}
              {currentSettlementData?.totalGstOnCommission
                ? `₹${currentSettlementData.totalGstOnCommission} /-`
                : "N/A"}
            </Typography>

            <Typography>
              <strong>Dinest Total Commission (with GST):</strong>{" "}
              {currentSettlementData?.dinestCommitionWithGst
                ? `₹${currentSettlementData.dinestCommitionWithGst} /-`
                : "N/A"}
            </Typography>

            <Typography>
              <strong>Restaurant Payout:</strong>{" "}
              {currentSettlementData?.restaurantPayout
                ? `₹${currentSettlementData.restaurantPayout} /-`
                : "N/A"}
            </Typography>

            {currentSettlementData?.isSettled ? (
              <>
                <Typography>
                  <strong>Payment Done On:</strong>{" "}
                  {currentSettlementData?.paidOn
                    ? new Date(currentSettlementData.paidOn).toLocaleDateString(
                        "en-GB",
                        {
                          day: "numeric",
                          month: "short",
                          year: "numeric",
                        }
                      )
                    : "N/A"}
                </Typography>

                <Typography>
                  <strong>Payment Mode:</strong>{" "}
                  {currentSettlementData?.paymentMode ?? "N/A"}
                </Typography>

                <Typography>
                  <strong>Transaction Id:</strong>{" "}
                  {currentSettlementData?.transactionId ?? "N/A"}
                </Typography>
              </>
            ) : (
              <Box
                sx={{
                  width: "60%",
                  display: "flex",
                  justifyContent: "center",
                  gap: 2,
                  alignSelf: "center",
                  flexDirection: "column",
                }}
              >
                <LocalizationProvider dateAdapter={AdapterDayjs}>
                  <DesktopDatePicker
                    label="Paid On"
                    name="paidOn"
                    inputFormat="DD/MM/YYYY"
                    format="DD/MM/YYYY"
                    fullWidth
                    value={
                      currentSettlementData?.paidOn
                        ? dayjs(currentSettlementData?.paidOn)
                        : null
                    } // Convert date to Dayjs object
                    className={styles.datePicker}
                    onChange={(e) => handleDateChange(e, "paidOn")} // Using handleDateChange
                  />
                </LocalizationProvider>

                <TextField
                  label="Payment Mode"
                  type="text"
                  fullWidth
                  variant="outlined"
                  name="paymentMode"
                  value={currentSettlementData.paymentMode}
                  onChange={handleChange}
                  className={styles.inputTextfield}
                />
                <TextField
                  label="Transaction Id"
                  type="text"
                  fullWidth
                  variant="outlined"
                  name="transactionId"
                  value={currentSettlementData?.transactionId}
                  onChange={handleChange}
                  className={styles.inputTextfield}
                />
              </Box>
            )}

            {!currentSettlementData?.isSettled && (
              <Button
                onClick={updateSettlement}
                variant="contained"
                className={styles.settleButton}
              >
                Mark as settled
              </Button>
            )}
          </Box>
        </DialogContent>
      </Dialog>
    </Box>
  );
};

export default Settlement;
